import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { AppSection, canAccessSection } from '../models/section-permissions.model';
import { TripPermissionsService } from '../services/trip-permissions.service';
import { AlertasService } from '../../Layouts/Components/alertas/alertas.service';

/** Shown when a URL leads somewhere the role may not go. */
export const SECTION_DENIED_NOTICE = 'No tienes permiso para acceder a esta sección.';

/**
 * Lets a route through only for the roles `canAccessSection` allows, read from the route's
 * `data.section`. Anyone else lands on the dashboard with a notice saying why.
 *
 * Hiding the menu entries is not enough on its own: the URL can still be typed, bookmarked
 * or left over from another account's session, and that is what this closes.
 *
 * Waits for the role to resolve rather than reading the signal, which starts at 'unknown'
 * and would bounce an administrator on a fresh page load. Guards run parent-first, so
 * authGuard has already restored the session the lookup needs.
 *
 * This is the front end only — the API itself must still refuse these calls.
 */
export const sectionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const section = route.data['section'] as AppSection;
  const permissions = inject(TripPermissionsService);
  const router = inject(Router);
  const alertas = inject(AlertasService);

  return permissions.resolveRole().pipe(
    take(1),
    map(role => {
      if (canAccessSection(role, section)) {
        return true;
      }
      alertas.alert({
        title: 'Acceso restringido',
        message: SECTION_DENIED_NOTICE,
        subtitle: '',
        confirmText: 'Entendido',
      });
      return router.parseUrl('/dashboard');
    }),
  );
};
